import { Spreadsheet } from '@progress/kendo-react-spreadsheet';
import "@progress/kendo-theme-default/dist/all.css";
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../store/hooks';
import { Box } from '@mui/material';
import { toast } from 'react-toastify';
import { fetchFilteredLandLordShare, postUpdateAgreementCropView } from '../../slices/serviceagreements.slice';
import CustomButton from '../../components/Button';
import { ServiceAgreementComponentProps } from './types';
import  styles  from './ServiceAgreements.module.scss';
import { ConfirmationDialog } from "../../components/ConfirmationDialog/ConfirmationDialog";

export const ServiceAgreementSpreadsheetComponent: React.FC<ServiceAgreementComponentProps> = ({ data, cropOptions, farmOptions, selectedYear, previousYear }) => {
    const [spreadSheetKey, setSpreadSheetKey] = useState<string | undefined>(new Date().toISOString());
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);
    const sheetRef = useRef<any>(null);
    const isUpdatedCostShare = useRef(false);
    const [resultArray, setResultArray] = useState<any[]>([]);
    const originalCellValue = useRef<Map<string, number>>(new Map());
    const dispatch = useAppDispatch();

    const handleCancel = () => {
        setShowCancelDialog(true);   
    }

    useEffect(() => {
        setSpreadSheetKey(new Date().toISOString())
        setResultArray([]);
    }, [data])

    const handleSaveDialogClose = () => setShowSaveDialog(false);
    const handleCancelDialogClose = () => setShowCancelDialog(false);
    const handleSaveYes = () => {
        setShowSaveDialog(false);
        handleSave();
    };
    
    const handleCancelYes = () => {
    setShowCancelDialog(false);
    dispatch(fetchFilteredLandLordShare({
        yearId: previousYear,
        "cropTypeIds": cropOptions.map(x => x.id),
        "landLordIds": farmOptions.map((x) => x.id)
    }));
    };

    const onClickSave = () =>{
        setShowSaveDialog(true);
    }
    const handleSave = async () => {
        if (resultArray.length === 0) {
            toast.warn("No changes are made !")
            return;
        }
        const result = await dispatch(postUpdateAgreementCropView(resultArray));
        if (result?.meta?.requestStatus === "fulfilled") {
            toast.success(result.payload);
            setResultArray([])
        }
        else if (result?.meta?.requestStatus === "rejected") {
            toast.error((result as any)?.error?.message)
            setResultArray([])
        }
        else {
            toast.error("Something went wrong")
        }
    }

    const updateRecordInExcel = (rows: any[] | undefined, costShare: number, ri: number, ci: number) => {
        let object = {
            "year": selectedYear,
            "costShare": 0,
            "landLordPartnerMappingId": 0,
            "operationProductCategoryId": 0
        };
        rows !== undefined && rows.forEach(row => {
            const rowIndex = row.index;
            object["costShare"] = costShare;
            const cells = row.cells;
            if (ri === rowIndex) {
                cells.forEach((item: any, index: any) => {
                    const columnIndex = item.index;
                    cells[columnIndex].value = costShare;
                    if (index === ci) {
                        item.value = costShare;
                        object["landLordPartnerMappingId"] = item.landLordPartnerMappingId;
                        object["operationProductCategoryId"] = item.operationProductCategoryId;
                        object["costShare"] = costShare;
                    }
                })
            }
        })
        return object
    }

    const handleSelectedCellValue = (e: any) => {
        const range = e.range;
        const row = range._ref.row;
        const col = range._ref.col;
        const cellKey = `${row}-${col}`;
        const workbook = sheetRef.current?.workbook;
        const sheet = workbook?.activeSheet();
        if (!sheet) return;

        const cell = sheet.range(row, col);
        const currentValue = (cell.value() ?? 0) * 100;

        if (!originalCellValue.current.has(cellKey)) {
            originalCellValue.current.set(cellKey, currentValue);
        }
    };

    const handleCellValueChange = (e: any) => {
        if (isUpdatedCostShare.current || !sheetRef.current) return;

        const range = e.range;
        const row = range._ref.topLeft.row;
        const col = range._ref.topLeft.col;
        const cellKey = `${row}-${col}`;
        let value = range.value();
        const data = e.sender.options.sheets;

        if (typeof value !== "string") {
            value = String(value);
        }
        value = value.trim();
        let extractedValue = value.match(/^\d+(\.\d+)?%?$/) ? value.replace("%", "") : NaN;
        let cellValue = parseFloat(extractedValue);
        const originalValue = originalCellValue.current.get(cellKey) ?? 0;
        if (isNaN(cellValue) || cellValue < 0 || cellValue > 100) {
            toast.error("Invalid Cost Share");
            cellValue = originalValue;
        } else {
            originalCellValue.current.set(cellKey, cellValue);
        }

        isUpdatedCostShare.current = true;

        try {
            const workbook = sheetRef.current.workbook;
            const sheet = workbook.activeSheet();
            if (sheet) {
                const cell = sheet.range(row, col);
                cell.value(cellValue / 100);   
                cell.format("0.00%");   
            }

            if (data !== undefined) {
                if (cellValue !== originalValue) {
                    const cellData = updateRecordInExcel(data[0].rows, cellValue, row, col);
                    setResultArray((prevData) => {
                        const updatedCellValue = prevData.filter(item =>
                            item.landLordPartnerMappingId !== cellData.landLordPartnerMappingId ||
                            item.operationProductCategoryId !== cellData.operationProductCategoryId
                        );
                        updatedCellValue.push({
                            year: cellData.year,
                            costShare: cellData.costShare,
                            landLordPartnerMappingId: cellData.landLordPartnerMappingId,
                            operationProductCategoryId: cellData.operationProductCategoryId
                        });
                        return updatedCellValue;
                    });
                }
            }
        }
        catch (error) {
            console.log(error);
        }
        finally {
            isUpdatedCostShare.current = false;
        }
    };

    return (
      <div>
        <ConfirmationDialog
          open={showSaveDialog}
          title="Save"
          description="Changes to cost sharing data will impact billing. Do you want to save the changes?"
          handleNo={handleSaveDialogClose}
          handleYes={handleSaveYes}
        />

        {/* Cancel confirmation dialog */}
        <ConfirmationDialog
          open={showCancelDialog}
          title="Warning"
          description="Are you sure you want to cancel? Any unsaved changes will be lost."
          handleNo={handleCancelDialogClose}
          handleYes={handleCancelYes}
        />
        {Object.keys(data).length == 0 ? (
          "Loading data"
        ) : (
          <>
            <Spreadsheet
              className={styles.spreadsheet}
              key={spreadSheetKey}
              ref={sheetRef}
              toolbar={false}
              style={{
                width: "100%",
                height: "80vh",
              }}
              onChange={handleCellValueChange}
              onSelect={handleSelectedCellValue}
              defaultProps={data}
            />
            <Box
              sx={{ display: "flex", justifyContent: "center", gap: "1rem" }}
            >
              <button onClick={handleCancel} className={styles.cancelButton}>
                {" "}
                Cancel
              </button>
              <CustomButton
                onClick={onClickSave}
                disabled={!resultArray.length}
                sx={{ width: "110px" }}
              >
                Save
              </CustomButton>
            </Box>
          </>
        )}
      </div>
    );
}